import { isSupabaseConfigured, supabase } from './supabase';

export type HomeTextPosition = 'underLogo' | 'underAds' | 'aboveIcons' | 'belowIcons';

export type HomeMenuItemSetting = {
  id: string;
  to: string | null;
  icon: string;
  label: string;
  desc: string;
  comingSoon?: boolean;
  visible: boolean;
  order: number;
};

export type HomeTextBlock = {
  id: string;
  text: string;
  position: HomeTextPosition;
  visible: boolean;
};

export type SiteDesignSettings = {
  logoSize: number;
  headingText: string;
  iconSize: number;
  cardPadding: number;
  gridGap: number;
  menuItems: HomeMenuItemSetting[];
  textBlocks: HomeTextBlock[];
};

const defaultMenuItems: HomeMenuItemSetting[] = [
  {
    id: 'faults',
    to: '/faults',
    icon: 'ri-tools-line',
    label: 'فحص أعطال الكهرباء',
    desc: 'شخّص الأعطال الكهربائية خطوة بخطوة بسهولة ودقة',
    visible: true,
    order: 0,
  },
  {
    id: 'simulator',
    to: '/simulator',
    icon: 'ri-cpu-line',
    label: 'محاكي الدوائر الكهربائية',
    desc: 'محاكي تفاعلي للدوائر الكهربائية',
    visible: true,
    order: 1,
  },
  {
    id: 'calculations',
    to: '/calculations',
    icon: 'ri-flashlight-line',
    label: 'الحسابات الكهربائية',
    desc: 'حساب التيار والقدرة ومقطع السلك والمقاومة',
    visible: true,
    order: 2,
  },
  {
    id: 'plc',
    to: '/plc',
    icon: 'ri-server-line',
    label: 'محاكي PLC',
    desc: 'برمجة وتوصيل ومحاكاة PLC',
    comingSoon: false,
    visible: true,
    order: 3,
  },
  {
    id: 'contact',
    to: '/contact',
    icon: 'ri-customer-service-2-line',
    label: 'تواصل معنا',
    desc: 'تواصل معنا عبر وسائل التواصل الاجتماعي',
    visible: true,
    order: 4,
  },
  {
    id: 'lighting',
    to: '/lighting',
    icon: 'ri-lightbulb-line',
    label: 'حساب الإضاءة المناسبة',
    desc: 'حساب الإضاءة المناسبة للمكان',
    visible: true,
    order: 5,
  },
  {
    id: 'products',
    to: '/products',
    icon: 'ri-store-2-line',
    label: 'خدماتنا',
    desc: 'تعرف على خدماتنا ومنتجاتنا المتاحة',
    visible: true,
    order: 6,
  },
  {
    id: 'ats-course',
    to: '/ats-course',
    icon: 'ri-graduation-cap-line',
    label: 'كورس ATS',
    desc: 'نظام التحويل التلقائي — من الصفر حتى الاحتراف',
    visible: true,
    order: 7,
  },
];

export const defaultSiteDesignSettings: SiteDesignSettings = {
  logoSize: 400,
  headingText: 'المهندس للتأسيسات والسيطرة الكهربائية',
  iconSize: 80,
  cardPadding: 32,
  gridGap: 20,
  menuItems: defaultMenuItems,
  textBlocks: [],
};

const LOCAL_KEY = 'murta-site-design-v2';

function clamp(value: unknown, min: number, max: number, fallback: number) {
  const number = Number(value);
  if (!Number.isFinite(number)) return fallback;
  return Math.min(max, Math.max(min, Math.round(number)));
}

function mergeMenuItems(value: unknown) {
  const incoming = Array.isArray(value) ? value : [];
  const incomingIds = new Set(
    incoming
      .filter((item) => item && typeof item === 'object' && typeof (item as { id?: unknown }).id === 'string')
      .map((item) => String((item as { id: string }).id)),
  );
  const hasCompleteSet = defaultMenuItems.every((item) => incomingIds.has(item.id));

  const merged = defaultMenuItems.map((base) => {
    const match = incoming.find(
      (item) => item && typeof item === 'object' && (item as { id?: unknown }).id === base.id,
    ) as Partial<HomeMenuItemSetting> | undefined;

    return {
      ...base,
      label: typeof match?.label === 'string' && match.label.trim() ? match.label : base.label,
      visible: typeof match?.visible === 'boolean' ? match.visible : base.visible,
      order:
        hasCompleteSet && Number.isFinite(Number(match?.order))
          ? Number(match?.order)
          : base.order,
    };
  });

  return merged
    .sort((a, b) => a.order - b.order)
    .map((item, order) => ({ ...item, order }));
}

export function normalizeSiteDesignSettings(value: unknown): SiteDesignSettings {
  const data = value && typeof value === 'object' ? (value as Partial<SiteDesignSettings>) : {};
  const textBlocks = Array.isArray(data.textBlocks)
    ? data.textBlocks
        .filter((item) => item && typeof item === 'object')
        .map((item) => {
          const block = item as Partial<HomeTextBlock>;
          const position: HomeTextPosition = ['underLogo', 'underAds', 'aboveIcons', 'belowIcons'].includes(String(block.position))
            ? (block.position as HomeTextPosition)
            : 'aboveIcons';
          return {
            id: typeof block.id === 'string' && block.id ? block.id : crypto.randomUUID(),
            text: typeof block.text === 'string' ? block.text : '',
            position,
            visible: typeof block.visible === 'boolean' ? block.visible : true,
          };
        })
        .slice(0, 20)
    : [];

  return {
    logoSize: clamp(data.logoSize, 220, 520, defaultSiteDesignSettings.logoSize),
    headingText:
      typeof data.headingText === 'string' && data.headingText.trim()
        ? data.headingText
        : defaultSiteDesignSettings.headingText,
    iconSize: clamp(data.iconSize, 56, 112, defaultSiteDesignSettings.iconSize),
    cardPadding: clamp(data.cardPadding, 18, 48, defaultSiteDesignSettings.cardPadding),
    gridGap: clamp(data.gridGap, 8, 32, defaultSiteDesignSettings.gridGap),
    menuItems: mergeMenuItems(data.menuItems),
    textBlocks,
  };
}

export function loadLocalSiteDesignSettings() {
  if (typeof window === 'undefined') return defaultSiteDesignSettings;
  try {
    const raw = localStorage.getItem(LOCAL_KEY);
    return raw ? normalizeSiteDesignSettings(JSON.parse(raw)) : defaultSiteDesignSettings;
  } catch {
    return defaultSiteDesignSettings;
  }
}

export async function loadSiteDesignSettings() {
  const local = loadLocalSiteDesignSettings();
  const client = supabase;
  if (!isSupabaseConfigured || !client) return { settings: local, remote: false };

  const { data, error } = await client.from('site_settings').select('config').eq('id', 'home').maybeSingle();
  if (error || !data?.config) return { settings: local, remote: false, error: error?.message };

  const settings = normalizeSiteDesignSettings(data.config);
  try {
    localStorage.setItem(LOCAL_KEY, JSON.stringify(settings));
  } catch {
    // Local cache is optional.
  }
  return { settings, remote: true };
}

export async function saveSiteDesignSettings(input: SiteDesignSettings) {
  const settings = normalizeSiteDesignSettings(input);
  if (typeof window !== 'undefined') {
    localStorage.setItem(LOCAL_KEY, JSON.stringify(settings));
  }

  const client = supabase;
  if (!isSupabaseConfigured || !client) {
    return { settings, remote: false, error: 'Supabase غير مربوط.' };
  }

  const { error } = await client.from('site_settings').upsert({
    id: 'home',
    config: settings,
    updated_at: new Date().toISOString(),
  });

  return { settings, remote: !error, error: error?.message };
}

export function resetLocalSiteDesignSettings() {
  if (typeof window !== 'undefined') localStorage.removeItem(LOCAL_KEY);
  return normalizeSiteDesignSettings(defaultSiteDesignSettings);
}
